import {
  CloseOutlined,
  DownloadOutlined,
  ExpandAltOutlined,
  LinkOutlined,
} from '@ant-design/icons'
import { AppModal } from '@components'
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer'
import { t } from 'i18next'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { Button, Input } from 'src/common'
import { IApplicant } from 'src/interfaces/application-management'
import styled from 'styled-components'

type IProps = {
  item?: IApplicant
  open?: boolean
  onClose?: () => void
}
type DocSelected = { uri: string; name?: string }[]

export const ViewDetailCV = ({ item, open, onClose }: IProps) => {
  const listPDF: DocSelected = useMemo(() => {
    const list = []
    if (item?.curriculumVitae?.url) {
      list.push({
        uri: item?.curriculumVitae?.url,
        name: item.curriculumVitae?.name,
      })
    }
    if (item?.coverLetter?.url) {
      list.push({
        uri: item?.coverLetter?.url,
        name: item?.coverLetter.name,
      })
    }
    return list
  }, [item])

  const [indexCv, setIndexCv] = useState(0)
  const [isExpand, setIsExpand] = useState(false)

  const handleChangeFile = (index: number) => {
    setIndexCv(index)
  }

  const downloadFile = async (urls: DocSelected) => {
    for (let i = 0; i < urls.length; i++) {
      const response = await fetch(urls[i].uri)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${urls[i].name}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)
    }
  }

  return (
    <AppModal
      open={open}
      onClose={() => {
        onClose?.()
      }}
      titleClassName="!pt-5"
      title={
        <div className="flex justify-end px-4 gap-x-5">
          <ExpandAltOutlined
            onClick={() => setIsExpand((pre) => !pre)}
            className="cursor-pointer"
          />
          <CloseOutlined className="cursor-pointer" onClick={onClose} />
        </div>
      }
      contentClassName={`${
        isExpand ? '!w-[100%] !h-[100%]' : 'max-w-[95%]'
      } min-w-[80%] min-h-[90%] !px-0`}
      childrenClassName="!mt-4"
    >
      <div className="grid grid-cols-2 gap-x-10 px-10">
        <div className="w-full">
          <div className="grid grid-cols-2 gap-y-4 gap-x-4">
            <h2 className="text-lg">Application information</h2>
            <Input
              alignment="col"
              label={t('evolveLanding:job_title')}
              placeholder="Marketing & Brand Specialist"
              disabled
              value={item?.jobPosting.title}
              inputStyledClassName="col-span-2"
              className="!h-[38px]"
            />
            <Input
              alignment="col"
              label={t('evolveLanding:location')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-2"
              value={item?.jobPosting?.address}
              className="!h-[38px]"
            />
            <Input
              alignment="col"
              label={'Job type'}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-1"
              className="!h-[38px]"
              value={item?.jobPosting?.type}
            />
            <Input
              alignment="col"
              label={t('evolveLanding:date_expired')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-1"
              className="!h-[38px]"
              value={moment(item?.jobPosting?.dateExpired).format('YYYY-MM-DD')}
            />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-y-4 gap-x-4">
            <h2 className="col-span-2 text-lg">Application detail</h2>
            <Input
              alignment="col"
              label={t('evolveLanding:full_name')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-1"
              className="!h-[38px]"
              value={item?.fullName}
            />
            <Input
              alignment="col"
              label={t('evolveLanding:user_permission')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-1"
              className="!h-[38px]"
              value={item?.isViewNextTime ? 'Yes' : 'No'}
            />
            <Input
              alignment="col"
              label={t('evolveLanding:email_address')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-2"
              className="!h-[38px]"
              value={item?.email}
            />
            <Input
              alignment="col"
              label={t('evolveLanding:phone_number')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-2"
              className="!h-[38px]"
              value={item?.phoneNumber}
            />
            <Input
              alignment="col"
              label={t('evolveLanding:location_more')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-2"
              className="!h-[38px]"
              value={item?.location}
            />
            <Input
              alignment="col"
              label={t('evolveLanding:additional_notes')}
              placeholder="Marketing & Brand Specialist"
              disabled
              inputStyledClassName="col-span-2"
              className="!h-[38px]"
              value={item?.additionalInfo}
            />
          </div>
          <div className="grid grid-cols-2 items-center mt-6 gap-y-4 gap-x-4">
            <h2>{t('evolveLanding:documents')}</h2>
            <Button
              onClick={() => downloadFile(listPDF)}
              className="ml-auto"
              type="primary"
            >
              {t('evolveLanding:download_all')}
            </Button>
            {listPDF.map((item, index) => (
              <div
                key={index}
                onClick={() => handleChangeFile(index)}
                className={`border-[#F3F4F6FF] bg-[#FAFAFBFF] border-2 rounded-lg px-2 py-2 border-dashed col-span-2 flex justify-center items-center cursor-pointer `}
              >
                <div
                  className={`flex items-center ${
                    index === indexCv ? 'text-[#009BB7FF] ' : ''
                  }`}
                >
                  <LinkOutlined className="text-lg" />
                  <span className="ml-2 text-base w-10/12 max-w-[350px] truncate inline-block">
                    {item.name}
                  </span>
                </div>
                <DownloadOutlined
                  onClick={() => downloadFile([item])}
                  className="text-lg ml-auto hover:text-[#009BB7FF]"
                />
              </div>
            ))}
          </div>
        </div>
        <StyleDocView>
          <DocViewer
            className="[&_.]:!w-full"
            style={{ width: '100%' }}
            documents={listPDF}
            activeDocument={listPDF[indexCv]}
            pluginRenderers={DocViewerRenderers}
            config={{
              header: { disableHeader: true },
              pdfZoom: {
                defaultZoom: isExpand ? 1.4 : 1, // 1 as default,
                zoomJump: 0.2, // 0.1 as default,
              },
            }}
          />
        </StyleDocView>
      </div>
    </AppModal>
  )
}

const StyleDocView = styled.div`
  width: fit-content;
  min-width: 670px;
  background: red;
  #react-doc-viewer {
    width: 100%;
  }
  #msdoc-renderer{
    height: 90vh !important;
  }
  .react-pdf__Page__canvas {
    width: 100% !important;
  }
  #pdf-controls {
    box-shadow: none;
  }
  #pdf-controls #pdf-download,
  #pdf-zoom-out,
  #pdf-zoom-in,
  #pdf-toggle-pagination,
  #pdf-zoom-reset {
    display: none;
  }
  #pdf-controls #pdf-pagination-next {
    margin-right: 0;
  }
`
